import React, { useState } from "react";
import CustomSlider from "../../../components/slider/CustomSlider";
import { DataTable } from "primereact/datatable";
import { GrowthPerecentageHeaders } from "../NiaOpportunityConstants";
import { Column } from "primereact/column";
import { Loader, SelectPicker, Tooltip, Whisper } from "rsuite";
import WarnArrowUpSm from "../../../assets/down-arrow-sm-danger.svg";
import SafeArrowDownSm from "../../../assets/down-arrow-sm-safe.svg";
import InfoOutline from "../../../assets/info-outline.svg";
import InfoOutlineIcon from "@rsuite/icons/InfoOutline";
import Button from "../../../components/button";
import {
  NiaOpportunityBestBenchmark,
  NiaOpportunityCommodityFilters,
  NiaOpportunityPeerGrowthBenchmark,
} from "../../../services/interfaces/INiaOpportunity";
import EditIconDark from "../../../assets/EditIconDark.svg";
import CheckIcon from "@rsuite/icons/Check";
import { InputNumber, InputNumberChangeEvent } from "primereact/inputnumber";
import { formatNumbersToFourDigits } from "../../../utils/formatter";
import { Dialog } from "primereact/dialog";
import { appInsights } from "../../../router/router";
import { sortCommodityFirst } from "../../../utils/helpers/commodityOrder";

interface INiaOppDetailsSectionProps {
  niaBaseOpportunity: number;
  niaGrowthOpportunity: number;
  niaBaseDiff: number | null;
  sliderPercentage: number;
  commodityFiltersList: NiaOpportunityCommodityFilters[];
  selectedCommodityFilter?: NiaOpportunityCommodityFilters;
  peerBenchmarkList?: NiaOpportunityPeerGrowthBenchmark[];
  bestBenchmark: NiaOpportunityBestBenchmark[] | undefined;
  setBestBenchmark: (value: NiaOpportunityBestBenchmark[] | undefined) => void;
  peerBenchmark: NiaOpportunityPeerGrowthBenchmark | undefined;
  setPeerBenchmark: (
    value: NiaOpportunityPeerGrowthBenchmark | undefined
  ) => void;
  resetHandler: () => void;
  handleSubmitChanges: () => void;
  handleExport: () => void;
  handleCategorySelected: (value: string | null) => void;
  vendorName: string;
  spendYear: string;
  selectedBaseRebate: number;
  selectedSpend: number;
  isPeerBenchmarkDetailsLoading: boolean;
  editMode: boolean;
  setEditMode: (value: boolean) => void;
  isValidInputBenchmark: () => boolean;
}

const NiaOppDetailsSection = ({
  niaBaseOpportunity,
  niaGrowthOpportunity,
  sliderPercentage,
  commodityFiltersList,
  selectedCommodityFilter,
  peerBenchmarkList,
  bestBenchmark,
  peerBenchmark,
  setPeerBenchmark,
  setBestBenchmark,
  resetHandler,
  niaBaseDiff,
  handleCategorySelected,
  vendorName,
  handleSubmitChanges,
  handleExport,
  spendYear,
  isPeerBenchmarkDetailsLoading,
  editMode,
  selectedBaseRebate,
  selectedSpend,
  setEditMode,
  isValidInputBenchmark,
}: INiaOppDetailsSectionProps) => {
  const isFormattingDigitsRequired = (num: any) => {
    if (typeof num !== "number") return false;
    const numStr = num.toString();
    const digits = numStr.replace(".", "");
    return digits.length > 4;
  };

  const [showBenchmarkPopup, setShowBenchMarkPopup] = useState(false);
  const [trackBenchmarkChange, setTrackBenchmarkChange] = useState<
    string | undefined
  >();
  const vendorTemplate = (rowData: NiaOpportunityBestBenchmark) => {
    if (rowData.isEditable) {
      return (
        <span className="flex flex-row justify-start items-center">
          {rowData.vendor}
          {!editMode ? (
            <img
              src={EditIconDark}
              alt="Edit"
              className="ml-[0.5vw] max-w-[0.625vw] min-w-[0.625vw] cursor-pointer"
              onClick={() => setEditMode(true)}
            />
          ) : (
            <CheckIcon
              className="ml-[0.5vw] cursor-pointer"
              color="black"
              style={{ fontSize: "0.7vw" }}
              onClick={() => {
                let validation = isValidInputBenchmark();
                if (!validation) {
                  setEditMode(true);
                  setShowBenchMarkPopup(true);
                } else {
                  setEditMode(false);
                  setShowBenchMarkPopup(false);
                  appInsights.trackEvent({
                    name: "NIA Opportunity",
                    properties: {
                      description:
                        trackBenchmarkChange || "Peer Benchmark Edited",
                    },
                  });
                }
              }}
            />
          )}
        </span>
      );
    }
    return rowData.vendor;
  };

  const updatedBestBenchmarkPercentages = (
    updatedValue: number | null,
    rowData: NiaOpportunityBestBenchmark,
    field: string
  ) => {
    (rowData as any)[field] = updatedValue ?? 0;

    const updatedBestBenchmark = bestBenchmark?.map((benchmarkData) =>
      benchmarkData.vendor === rowData.vendor ? rowData : benchmarkData
    );

    setBestBenchmark(JSON.parse(JSON.stringify(updatedBestBenchmark)));
    setTrackBenchmarkChange(
      `NIA Opportunity changed the value of the benchmark from ${JSON.stringify(
        rowData
      )} to  ${JSON.stringify(updatedBestBenchmark)}`
    );
  };

  const PercentageTemplate = (
    rowdata: NiaOpportunityBestBenchmark,
    field: string,
    currentValue: number
  ) => {
    if (typeof currentValue === "number") {
      if (rowdata.isEditable) {
        if (editMode) {
          return (
            <span className="w-full">
              <InputNumber
                maxFractionDigits={2}
                value={currentValue}
                onChange={(event: InputNumberChangeEvent) => {
                  if (event?.value && isNaN(event?.value)) {
                    return;
                  }
                  updatedBestBenchmarkPercentages(event.value, rowdata, field);
                }}
                className="custom-input-number-for-benchmark"
              />
            </span>
          );
        } else {
          return (
            <span className="flex justify-center items-center">
              {isFormattingDigitsRequired(currentValue) ? (
                <Whisper speaker={<Tooltip>{currentValue}</Tooltip>}>
                  {formatNumbersToFourDigits(currentValue) + "..."}
                </Whisper>
              ) : (
                currentValue
              )}
              %
            </span>
          );
        }
      } else {
        return (
          <span className="flex justify-center items-center">
            {isFormattingDigitsRequired(currentValue) ? (
              <Whisper speaker={<Tooltip>{currentValue}</Tooltip>}>
                {formatNumbersToFourDigits(currentValue) + "..."}
              </Whisper>
            ) : (
              currentValue
            )}
            %
          </span>
        );
      }
    }
    return (
      <span className="flex justify-center items-center">
        {isFormattingDigitsRequired(currentValue) ? (
          <Whisper speaker={<Tooltip>{currentValue}</Tooltip>}>
            {formatNumbersToFourDigits(currentValue) + "..."}
          </Whisper>
        ) : (
          currentValue
        )}
        %
      </span>
    );
  };

  const PeerCategorySelectionTemplate = (
    rowData: NiaOpportunityPeerGrowthBenchmark
  ) => {
    return (
      <div className="flex flex-row justify-center items-center">
        <input
          type="radio"
          onChange={() => setPeerBenchmark(rowData)}
          checked={rowData.peer_group === peerBenchmark?.peer_group}
          className="peer-selection-input"
        />
      </div>
    );
  };

  return (
    <div className="bg-white shadow-module-card flex flex-col justify-start items-stretch w-full h-full gap-[2vh] p-[2vw]">
      <div className="flex flex-row flex-nowrap shrink-0 gap-6">
        <div className="basis-[50%] shadow-module-card py-[1vh] px-pd13 W rounded-md border border-[#bebebe] w-full">
          <div className="flex flex-row flex-nowrap items-center justify-between">
            <div className="font-bold text-fs16">NIA Base Opportunity</div>
            <div>
              <div className="font-normal text-fs32">
                {`$${niaBaseOpportunity.toFixed(2)}M`}
              </div>
              {niaBaseDiff !== null && (
                <div className="flex justify-end">
                  <div className="flex">
                    <div className={`flex text-fs14 text-alert`}>
                      {`$${Math.abs(niaBaseDiff).toFixed(2)}M`}
                    </div>
                    <div className="flex justify-center items-center">
                      {
                        <img
                          className="w-[0.6vw] h-[0.6vw]"
                          src={WarnArrowUpSm}
                          alt="safe"
                        ></img>
                      }
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="basis-[50%] shadow-module-card flex justify-between items-center rounded-md border border-[#bebebe] py-[1.2vh] px-pd13 w-full">
          <span className="flex flex-col flex-nowrap justify-start items-start">
            <span className="font-bold text-fs14">NIA Growth Opportunity</span>
          </span>
          <span className="flex h-full self-end gap-4 font-normal text-fs32 items-center">
            {`$${niaGrowthOpportunity.toFixed(2)}M`}
          </span>
        </div>
      </div>
      <div className="flex flex-col flex-nowrap shrink-0 items-start justify-start">
        <div className="w-full flex  items-center shadow-module-card border border-[#bebebe] bg-white px-pd13 py-[1.5vh] font-bold text-[0.7vw] rounded-md">
          <div className="mr-[1vh]">Spend Growth % and Rebate Tiers</div>
          <div className="flex items-center justify-center">
            <Whisper
              speaker={
                <Tooltip placement="auto" className="p-1 text-[0.7vw]">
                  <div>
                    {`${bestBenchmark ? bestBenchmark[1]?.vendor : ''} is ${bestBenchmark ? bestBenchmark[1]?.benchmark_name : ''}, click on the pen icon to customize growth rebate`}
                  </div>
                </Tooltip>
              }
            >
              <InfoOutlineIcon
                style={{
                  fontSize: "2vh",
                  cursor: "pointer",
                  color: "grey",
                  position: "relative",
                  top: "0.5px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              />
            </Whisper>
          </div>
        </div>
        <div className="flex w-full mt-[1vh]">
          <CustomSlider pivot={sliderPercentage} />
        </div>
        <div className="w-full nia-opportunity-benchmark-table">
          <DataTable
            value={bestBenchmark}
            stripedRows
            emptyMessage="No data found corresponding to selected commodity."
            dataKey={"vendor"}
            // showGridlines
            columnResizeMode={"fit"}
          >
            {GrowthPerecentageHeaders.map((column, index) => (
              <Column
                key={column.field}
                header={column.header}
                field={column.field}
                style={
                  column.field === "vendor"
                    ? {}
                    : { minWidth: "2.75vw", maxWidth: "2.75vw" }
                }
                body={
                  column.field === "vendor"
                    ? vendorTemplate
                    : (rowData) =>
                      PercentageTemplate(
                        rowData,
                        column.field,
                        rowData[column.field]
                      )
                }
              />
            ))}
          </DataTable>
        </div>
      </div>
      <div>
        <div className="flex flex-row justify-between items-center flex-nowrap shrink-0 w-full shadow-module-card border border-[#bebebe] rounded-md bg-white px-pd13 py-[1vh]">
          <div className="flex items-center">
            <span className="font-bold text-[0.7vw]">
              Category Wise Peer Benchmarks for{" "}
            </span>
            <span className="font-bold text-[0.8vw] text-deloitteGreen max-w-[13vw] pl-[0.5vh] line-clamp-1">
              {vendorName}
              {` ($${selectedSpend}M, ${selectedBaseRebate}%)`}
            </span>
          </div>
          <div>
            <SelectPicker
              value={selectedCommodityFilter?.commodity}
              data={sortCommodityFirst(commodityFiltersList)}
              labelKey="commodity"
              valueKey="commodity"
              menuClassName="category-wise-peer-benchmarks-menu-dropdown"
              cleanable={false}
              onChange={(value) => {
                handleCategorySelected(value);
              }}
              className="nia-commodity-select"
            />
          </div>
        </div>
      </div>
      <div className="w-full h-fit nia-opportunity-category-peer-table max-h-[18vh]">
        {isPeerBenchmarkDetailsLoading ? (
          <Loader
            content="Loading..."
            size="md"
            vertical
            className="text-black font-bold"
            style={{
              opacity: 0.6,
              zIndex: 1105,
              width: "100%",
              height: "18vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          />
        ) : (
          <DataTable
            value={peerBenchmarkList}
            showGridlines
            stripedRows
            scrollable
            isDataSelectable={(event) =>
              !(event.data.peer_base_rebate < selectedBaseRebate)
            }
            rowClassName={(rowData) =>
              !(rowData.peer_base_rebate < selectedBaseRebate)
                ? "cursor-pointer"
                : "cursor-not-allowed datatable-disabled"
            }
            scrollHeight="16vh"
            emptyMessage="No data found corresponding to selected commodity."
            selectionMode={"single"}
            selection={peerBenchmark!}
            onSelectionChange={(e: any) => {
              if (e && e?.value !== null && e?.value !== peerBenchmark) {
                setPeerBenchmark(e.value);
              }
            }}
            showHeaders
            dataKey={"peer_group"}
            columnResizeMode={"fit"}
          >
            <Column
              headerStyle={{ width: "0.5vw" }}
              body={(rowData) => {
                return (
                  <div className="flex flex-row justify-center items-center">
                    <input
                      disabled={rowData.peer_base_rebate < selectedBaseRebate}
                      type="radio"
                      onChange={() => setPeerBenchmark(rowData)}
                      checked={rowData.peer_group === peerBenchmark?.peer_group}
                      className="peer-selection-input"
                    />
                  </div>
                );
              }}
            />
            <Column
              bodyClassName={(rowData) => {
                return rowData.peer_base_rebate <= selectedBaseRebate
                  ? "text-disabled"
                  : "";
              }}
              field="peer_group"
              header="Supplier"
            />
            <Column
              bodyClassName={(rowData) => {
                return rowData.peer_base_rebate <= selectedBaseRebate
                  ? "text-disabled"
                  : "";
              }}
              field="spend"
              header={`${spendYear} Spend`}
              body={(rowData) =>
                `${Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                  maximumFractionDigits: 1,
                }).format(rowData.spend)}M`
              }
            />
            <Column
              bodyClassName={(rowData) => {
                return rowData.peer_base_rebate <= selectedBaseRebate
                  ? "text-disabled"
                  : "";
              }}
              field="effective_rebate"
              header="Base Rebate %"
              body={(rowData) => `${rowData.peer_base_rebate}%`}
            />
          </DataTable>
        )}
      </div>
      <div className="flex flex-row justify-between items-center flex-nowrap shrink-0 w-full gap-6">
        <Button
          label="Reset"
          bgColor="#86BC25"
          size="md"
          className="basis-[33.33%]"
          onClick={resetHandler}
        />
        <Button
          label="Download"
          bgColor="#86BC25"
          size="md"
          className="basis-[33.33%]"
          onClick={handleExport}
        />
        <Button
          label="Submit Changes"
          bgColor="#86BC25"
          size="md"
          className="basis-[33.33%]"
          disabled={editMode}
          onClick={handleSubmitChanges}
        />
      </div>
      <Dialog
        draggable={false}
        visible={showBenchmarkPopup}
        modal
        headerClassName="custom-dialog-header-nia below-1000:h-[32px]"
        focusOnShow={false}
        showHeader={true}
        style={{ width: "30vw", height: "8vw", paddingBottom: 0 }}
        closable={true}
        onHide={() => {
          setShowBenchMarkPopup(false);
        }}
      >
        <div className="flex justify-center items-center gap-2 h-full w-full text-center">
          <img className="h-[1vw]" src={InfoOutline}></img>
          <span className="text-fs16 font-semibold">
            Enter benchmark growth tier % higher than supplier's current tier %
          </span>
        </div>
      </Dialog>
    </div>
  );
};

export default NiaOppDetailsSection;
